'use client';
import { Box } from '@mui/material';

import { EmptyState, ErrorState, HomeMarketCardSkeleton, LoadMoreButton } from '@/components';
import { useT } from '@/hooks';
import type { EventResponse } from '@/types';
import { MarketListByTab } from './MarketListByTab';

// Cards never shrink below this width — column count is however many fit,
// at any viewport size, instead of a fixed count per breakpoint.
const MIN_CARD_WIDTH = 260;
export const GRID_TEMPLATE_COLUMNS = `repeat(auto-fit, minmax(${MIN_CARD_WIDTH}px, 1fr))`;

interface HomeContentProps {
  isLoading: boolean;
  error: unknown;
  events: EventResponse[];
  activeCategorySlug: string;
  selectedFilter?: string;
  pagination: {
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
  };
  onTradeClick: (marketId: string, outcomeId: string, isBuyOrder: boolean) => void;
  onFetchNextPage: () => void;
  onRetry: () => void;
  isResolvedFilter?: boolean;
}

export const HomeContent = ({
  isLoading,
  error,
  events,
  activeCategorySlug,
  selectedFilter,
  pagination,
  onTradeClick,
  onFetchNextPage,
  onRetry,
  isResolvedFilter,
}: HomeContentProps) => {
  const { t } = useT();

  if (isLoading) {
    return <HomeMarketCardSkeleton gridStyles={GRID_TEMPLATE_COLUMNS} />;
  }

  if (error) {
    return (
      <ErrorState title="Failed to load markets" message="Please try again." onRetry={onRetry} />
    );
  }

  if (!events || events.length === 0) {
    return (
      <EmptyState
        title={t('sports.noMarketsAvailable', { defaultValue: 'No markets available' })}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
          gap: 1.5,
          width: '100%',
        }}
      >
        <MarketListByTab
          activeCategorySlug={activeCategorySlug}
          selectedFilter={selectedFilter}
          events={events}
          handleTradeClick={onTradeClick}
          isResolvedFilter={isResolvedFilter}
        />
      </Box>

      <LoadMoreButton
        onClick={onFetchNextPage}
        isLoading={pagination.isFetchingNextPage}
        hasNextPage={pagination.hasNextPage}
        events={events}
      />
    </>
  );
};
